import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { useEntitlements } from '../hooks/useEntitlements';
import { useLanguage } from '../contexts/LanguageContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { comics } from '../data/comics';

// Refactored components
import {
  HomeHeader,
  FeaturedSection,
  ComicLibrary,
  FreeComicsSection,
  HeroBanner,
  CURRENT_APP_VERSION,
} from '../components/home';
import { PromoComic } from '../components/home/PromoComic';
import { PROMO_IMAGE } from '../data/monthly';

// Payhip purchase URLs for each comic
const COMIC_BUY_URLS = {
  0: 'https://payhip.com/b/UkmBs',
  1: 'https://payhip.com/b/oG3Ux',
  2: null, // FREE
  3: 'https://payhip.com/b/FdfRj',
  4: 'https://payhip.com/b/pxAaF',
  5: 'https://payhip.com/b/FIePL',
  6: 'https://payhip.com/b/mnQql',
  7: 'https://payhip.com/b/52buC',
  8: 'https://payhip.com/b/5HSOo',
  9: 'https://payhip.com/b/eliH3',
  10: 'https://payhip.com/b/vqJAE',
  11: 'https://payhip.com/b/oTULZ',
  12: 'https://payhip.com/b/BRuc3',
  13: 'https://payhip.com/b/yKE1G',
  14: 'https://payhip.com/b/Z4GLo',
  15: 'https://payhip.com/b/aEzKm',
  16: 'https://payhip.com/b/BHWAf',
  17: 'https://payhip.com/b/OXNgh',
  18: 'https://payhip.com/b/Jos3H',
  19: 'https://payhip.com/b/2ioa5',
  20: 'https://payhip.com/b/VOt4P',
  21: 'https://payhip.com/b/8cyvP',
  22: 'https://payhip.com/b/woFCS',
  23: 'https://payhip.com/b/CX1Jc',
};

export { CURRENT_APP_VERSION };

export default function Home() {
  const { signOut, isMasterAccess } = useAuth();
  const { unlockedComicIds, isUnlocked } = useEntitlements();
  const { t } = useLanguage();
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');

  const handleSignOut = () => {
    signOut();
  };

  // Comic handlers
  const handleComicClick = (comicId) => {
    const comic = comics.find(c => c.id === comicId);
    if (comic?.locked && !isUnlocked(comicId)) {
      return;
    }
    navigate(`/comic/${comicId}`);
  };

  const handleUnlockClick = (comicId, e) => {
    e.stopPropagation();
    navigate(`/redeem?comic=${comicId}`);
  };

  const handleBuyClick = async (comicId, e) => {
    e.stopPropagation();
    const url = COMIC_BUY_URLS[comicId];
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fp-root" style={styles.container}>
      <div className={`fp-wall${isDarkMode ? ' fp-wall--dark' : ''}`} style={styles.wall} />
      <HomeHeader
        isMasterUser={isMasterAccess}
        onSignOut={handleSignOut}
      />

      <div style={styles.scrollContainer}>
        {/* The comic of the month: the first thing on the page */}
        <FeaturedSection
          unlockedComics={unlockedComicIds}
          unlockingComic={null}
          showUnlockInput={null}
          onComicClick={handleComicClick}
          onUnlockClick={handleUnlockClick}
          onBuyClick={handleBuyClick}
        />

        {/* Next month's comic, already out for the Master tier on Patreon */}
        <div className="fp-panel fp-rise" style={{ ...promoSectionStyle, '--i': 3 }}>
          <div className="fp-halftone" />
          <div style={promoRowStyle}>
            <PromoComic imageSrc={PROMO_IMAGE} reverse={false} />
          </div>
          <div style={promoTextColumnStyle}>
            <span className="fp-chip fp-chip--pink" style={promoTextLimitedStyle}>{t('promoLimitedAccess')}</span>
            <p style={promoTextTopStyle}>{t('promoMasterTier')}</p>
            <p style={promoTextBottomStyle}>{t('promoPublicRelease')}</p>
            <button
              className="fp-btn fp-btn--sm fp-btn--green"
              style={promoButtonStyle}
              onClick={() => navigate('/news')}
            >
              {t('promoSeeItNow')}
            </button>
          </div>
        </div>

        {/* This month's art on Patreon */}
        <HeroBanner />

        {/* Comic Library */}
        <ComicLibrary
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          unlockedComics={unlockedComicIds}
          unlockingComic={null}
          showUnlockInput={null}
          onComicClick={handleComicClick}
          onUnlockClick={handleUnlockClick}
          onBuyClick={handleBuyClick}
        />

        {/* Free Comics Section */}
        <FreeComicsSection />
      </div>
    </div>
  );
}

const promoSectionStyle = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '14px',
  margin: '18px 14px 0',
  padding: '14px',
};

const promoRowStyle = {
  position: 'relative',
  width: '36%',
  maxWidth: '130px',
  flexShrink: 0,
};

const promoTextColumnStyle = {
  position: 'relative',
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
};

const promoTextTopStyle = {
  fontSize: '15px',
  fontWeight: 900,
  fontStyle: 'italic',
  color: '#FFD700',
  margin: 0,
  lineHeight: '1.2',
  textShadow: 'var(--fp-outline-sm)',
};

const promoTextLimitedStyle = {
  fontSize: '10px',
};

const promoTextBottomStyle = {
  fontSize: '12.5px',
  fontWeight: 700,
  color: '#eadcff',
  margin: 0,
  lineHeight: '1.3',
};

const promoButtonStyle = {
  marginTop: '2px',
  textTransform: 'none',
  fontSize: '13px',
};

const styles = {
  wall: {
    backgroundImage: 'url(/wallpaper.png)',
  },
  container: {
    position: 'relative',
    minHeight: '100vh',
    backgroundColor: '#1b0a33',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollContainer: {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    overflowX: 'hidden',
    padding: '0',
  },
};
